import type { CatalogGroup } from './BigSkyEnrichmentShell';

/**
 * Canonical attribute catalog for the OWNED enrichment vertebra picker.
 *
 * Mirrors backend/src/enrichment/canonicalAttributes.ts (the source of
 * truth). These are OUR canonical keys — every source (DataMoon today,
 * partners later) is mapped into them at ingest, so this list is stable
 * regardless of any partner's field names. Built from a real DataMoon
 * export, including the derived numeric floors/ceilings for downstream
 * leg filtering (e.g. net_worth_min ≥ X).
 *
 * Keep in sync with the backend registry when attributes are added.
 */
export const OWNED_ATTRIBUTE_CATALOG: CatalogGroup[] = [
  {
    group: 'Identity',
    attributes: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'birth_date', label: 'Date of Birth' },
      { key: 'birth_year', label: 'Birth Year' },
    ],
  },
  {
    group: 'Contact',
    attributes: [
      { key: 'personal_email', label: 'Personal Email' },
      { key: 'additional_emails', label: 'Additional Emails' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_email_validation_status', label: 'Business Email Status' },
      { key: 'mobile_phone', label: 'Mobile Phone' },
      { key: 'personal_phones', label: 'Personal Phones' },
      { key: 'mobile_carrier', label: 'Mobile Carrier' },
      { key: 'mobile_dnc', label: 'Mobile on DNC (Do Not Call)' },
      { key: 'phone_type', label: 'Phone Type (primary)' },
      { key: 'personal_phone_types', label: 'Phone Types (all)' },
      { key: 'personal_phone_carriers', label: 'Phone Carriers (all)' },
      { key: 'personal_phone_dnc', label: 'Phone DNC Flags (all)' },
      { key: 'direct_numbers', label: 'Direct Numbers' },
      { key: 'personal_email_validation_status', label: 'Personal Email Status' },
      { key: 'programmatic_business_emails', label: 'Programmatic Business Emails (guessed)' },
      { key: 'email_optin', label: 'Email Opt-in' },
    ],
  },
  {
    group: 'Location',
    attributes: [
      { key: 'personal_address', label: 'Street Address' },
      { key: 'personal_address_2', label: 'Street Address 2' },
      { key: 'personal_city', label: 'City' },
      { key: 'personal_state', label: 'State' },
      { key: 'personal_zip', label: 'ZIP' },
      { key: 'personal_zip4', label: 'ZIP+4' },
      { key: 'contact_country', label: 'Country' },
      { key: 'latitude', label: 'Latitude' },
      { key: 'longitude', label: 'Longitude' },
      { key: 'urbanicity', label: 'Urbanicity' },
    ],
  },
  {
    group: 'Demographics',
    attributes: [
      { key: 'gender', label: 'Gender' },
      { key: 'age_range', label: 'Age Range' },
      { key: 'age_min', label: 'Age (min)' },
      { key: 'age_max', label: 'Age (max)' },
      { key: 'income_range', label: 'Income Range' },
      { key: 'income_min', label: 'Income (min $)' },
      { key: 'income_max', label: 'Income (max $)' },
      { key: 'net_worth', label: 'Net Worth' },
      { key: 'net_worth_min', label: 'Net Worth (min $)' },
      { key: 'net_worth_max', label: 'Net Worth (max $)' },
      { key: 'married', label: 'Married' },
      { key: 'children', label: 'Has Children' },
      { key: 'num_children_hh', label: 'Children in Household' },
      { key: 'num_adults_hh', label: 'Adults in Household' },
      { key: 'household_size', label: 'Household Size' },
      { key: 'homeowner', label: 'Homeowner' },
      { key: 'home_price', label: 'Home Price ($)' },
      { key: 'home_value', label: 'Home Value ($)' },
      { key: 'dwelling_type', label: 'Dwelling Type' },
      { key: 'length_of_residence', label: 'Length of Residence (yrs)' },
      { key: 'age', label: 'Age' },
      { key: 'generation', label: 'Generation' },
      { key: 'education', label: 'Education Level' },
      { key: 'veteran', label: 'Veteran' },
      { key: 'income_estimate', label: 'Income (Est. $)' },
      { key: 'net_worth_estimate', label: 'Net Worth (Est. $)' },
    ],
  },
  {
    group: 'Finance',
    attributes: [
      { key: 'financial_power', label: 'Financial Power' },
      { key: 'credit_range', label: 'Credit Range' },
      { key: 'credit_estimate', label: 'Credit Score (Est.)' },
      { key: 'owns_investments', label: 'Owns Investments' },
      { key: 'owns_stocks_bonds', label: 'Owns Stocks/Bonds' },
      { key: 'owns_mutual_funds', label: 'Owns Mutual Funds' },
      { key: 'has_credit_card', label: 'Has Credit Card' },
      { key: 'has_bank_card', label: 'Has Bank Card' },
      { key: 'num_credit_lines', label: 'Credit Lines' },
    ],
  },
  {
    group: 'Professional',
    attributes: [
      { key: 'job_title', label: 'Job Title' },
      { key: 'job_title_normalized', label: 'Job Title (normalized)' },
      { key: 'department', label: 'Department' },
      { key: 'department_alt', label: 'Department (alt)' },
      { key: 'seniority_level', label: 'Seniority Level' },
      { key: 'seniority_level_alt', label: 'Seniority Level (alt)' },
      { key: 'linkedin_url', label: 'LinkedIn URL' },
      { key: 'social_connections', label: 'LinkedIn Connections (band)' },
      { key: 'professional_address', label: 'Work Address' },
      { key: 'professional_address_2', label: 'Work Address 2' },
      { key: 'professional_city', label: 'Work City' },
      { key: 'professional_state', label: 'Work State' },
      { key: 'professional_zip', label: 'Work ZIP' },
      { key: 'professional_zip4', label: 'Work ZIP+4' },
      { key: 'occupation_category', label: 'Occupation' },
      { key: 'occupation_type', label: 'Occupation Type' },
      { key: 'occupation_detail', label: 'Occupation Detail' },
    ],
  },
  {
    group: 'Company',
    attributes: [
      { key: 'company_name', label: 'Company' },
      { key: 'company_domain', label: 'Company Domain' },
      { key: 'company_address', label: 'Company Address' },
      { key: 'company_city', label: 'Company City' },
      { key: 'company_state', label: 'Company State' },
      { key: 'company_zip', label: 'Company ZIP' },
      { key: 'company_country', label: 'Company Country' },
      { key: 'company_phones', label: 'Company Phones' },
      { key: 'company_linkedin_url', label: 'Company LinkedIn' },
      { key: 'primary_industry', label: 'Primary Industry' },
      { key: 'company_sic', label: 'SIC Codes' },
      { key: 'company_naics', label: 'NAICS Codes' },
      { key: 'company_revenue', label: 'Company Revenue' },
      { key: 'company_revenue_min', label: 'Company Revenue (min $)' },
      { key: 'company_revenue_max', label: 'Company Revenue (max $)' },
      { key: 'company_employee_count', label: 'Employee Count' },
      { key: 'company_employee_min', label: 'Employees (min)' },
      { key: 'company_employee_max', label: 'Employees (max)' },
      { key: 'company_description', label: 'Company Description' },
      { key: 'related_domains', label: 'Related Company Domains' },
      { key: 'cc_id', label: 'Co-Op Company ID' },
    ],
  },
  {
    group: 'Vehicle',
    attributes: [
      { key: 'vehicle_make', label: 'Vehicle Make' },
      { key: 'vehicle_model', label: 'Vehicle Model' },
      { key: 'vehicle_year', label: 'Vehicle Year' },
      { key: 'vehicle_vin', label: 'VIN' },
      { key: 'vehicle_fuel', label: 'Fuel Type' },
      { key: 'vehicle_body_type', label: 'Body Type' },
      { key: 'vehicle_msrp', label: 'MSRP ($)' },
      { key: 'vehicle_trim', label: 'Trim' },
    ],
  },
  {
    group: 'Digital',
    attributes: [
      { key: 'ip_address', label: 'IP Address' },
      { key: 'additional_ips', label: 'Additional IPs' },
      { key: 'ip_type', label: 'IP Type' },
      { key: 'device_id', label: 'Device ID' },
      { key: 'additional_device_ids', label: 'Additional Device IDs' },
      { key: 'device_type', label: 'Device Type' },
      { key: 'device_os', label: 'Device OS' },
    ],
  },
  {
    group: 'Scoring & Meta',
    attributes: [
      { key: 'confidence_score', label: 'Confidence Score (0-100)' },
      { key: 'score_category', label: 'Score Category' },
      { key: 'source_updated_at', label: 'Source Last Updated' },
      { key: 'personal_email_last_seen', label: 'Personal Email Last Seen' },
      { key: 'business_email_last_seen', label: 'Business Email Last Seen' },
      { key: 'job_title_last_updated', label: 'Job Title Last Updated' },
      { key: 'company_last_updated', label: 'Company Last Updated' },
    ],
  },
];
